'use client'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCallback, useEffect } from 'react'
import { toast } from 'sonner'

import { useCropperStore } from '@/stores/cropperStore'
import { AspectRatioSelector } from './AspectRatioSelector'
import { CropToolbar } from './CropToolbar'
import { ImageEditor } from './ImageEditor'
import { AspectRatioValues, type ImageCropperProps } from './types'
import { DEFAULT_ASPECT_RATIOS } from './utils/image'

export function ImageCropper({
  imageSrc,
  onCancel,
  onApply,
  defaultAspectRatio = AspectRatioValues.FREE,
  aspectRatios = DEFAULT_ASPECT_RATIOS,
}: ImageCropperProps) {
  const currentTransform = useCropperStore((state) => state.currentTransform)
  const isProcessing = useCropperStore((state) => state.isProcessing)
  const imageNaturalWidth = useCropperStore((state) => state.imageNaturalWidth)
  const imageNaturalHeight = useCropperStore((state) => state.imageNaturalHeight)

  const setAspectRatio = useCropperStore((state) => state.setAspectRatio)
  const setRotation = useCropperStore((state) => state.setRotation)
  const flipHorizontal = useCropperStore((state) => state.flipHorizontal)
  const flipVertical = useCropperStore((state) => state.flipVertical)
  const reset = useCropperStore((state) => state.reset)
  const setProcessing = useCropperStore((state) => state.setProcessing)

  useEffect(() => {
    reset({
      crop: { x: 15, y: 15, width: 70, height: 70 },
      rotation: 0,
      flipHorizontal: false,
      flipVertical: false,
      aspectRatio: defaultAspectRatio,
      zoom: 1,
    })
  }, [defaultAspectRatio, reset])

  const handleAspectRatioChange = useCallback(
    (value: AspectRatioValues) => {
      setAspectRatio(value)
    },
    [setAspectRatio],
  )

  const handleRotateLeft = useCallback(() => {
    setRotation((currentTransform.rotation - 90) % 360)
  }, [currentTransform.rotation, setRotation])

  const handleRotateRight = useCallback(() => {
    setRotation((currentTransform.rotation + 90) % 360)
  }, [currentTransform.rotation, setRotation])

  const handleApply = useCallback(async () => {
    if (!currentTransform.crop?.width || !currentTransform.crop?.height) {
      toast.error('No crop area selected')
      return
    }

    if (!imageNaturalWidth || !imageNaturalHeight) {
      toast.error('Image not loaded properly')
      return
    }

    const cropperRef = useCropperStore.getState().cropperRef
    if (!cropperRef) {
      toast.error('Cropper not ready')
      return
    }

    setProcessing(true)
    try {
      const canvas = cropperRef.getCanvas()
      if (canvas) {
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob: Blob | null) => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Failed to create blob from canvas'))
              }
            },
            'image/jpeg',
            0.95,
          )
        })
        onApply(blob)
      } else {
        toast.error('Failed to get cropped image')
      }
    } catch (error) {
      console.error('Error applying crop:', error)
      toast.error('Failed to process image')
    } finally {
      setProcessing(false)
    }
  }, [currentTransform, onApply, setProcessing, imageNaturalWidth, imageNaturalHeight])

  const handleCancel = useCallback(() => {
    reset()
    onCancel()
  }, [onCancel, reset])

  return (
    <Dialog open={true} onOpenChange={handleCancel}>
      <DialogContent className='max-w-7xl w-full h-full max-h-[95vh] sm:max-h-[90vh] p-3 sm:p-6 overflow-hidden'>
        <DialogHeader className='pb-2 sm:pb-4 flex justify-start w-full items-start'>
          <DialogTitle className='text-left text-lg sm:text-xl font-medium text-gray-900'>Crop Image</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col lg:flex-row gap-3 sm:gap-6 h-full min-h-0'>
          <div className='flex flex-col justify-between w-full lg:w-1/3 lg:min-w-[300px] lg:max-w-[350px] order-2 lg:order-1'>
            <div className='w-full flex flex-col gap-4 sm:gap-6 lg:gap-8 justify-start'>
              <AspectRatioSelector
                value={currentTransform.aspectRatio}
                onChange={handleAspectRatioChange}
                options={aspectRatios}
              />

              <CropToolbar
                onRotateLeft={handleRotateLeft}
                onRotateRight={handleRotateRight}
                onFlipHorizontal={flipHorizontal}
                onFlipVertical={flipVertical}
              />
            </div>

            <div className='flex flex-col sm:flex-row justify-between items-center gap-3 mt-4 lg:mt-6 pt-4 border-t lg:border-t-0 lg:pt-0'>
              <Button
                type='button'
                variant='outline'
                onClick={handleCancel}
                className='text-red-600 border-red-200 hover:bg-red-50 w-full sm:flex-1'
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                type='button'
                onClick={handleApply}
                disabled={isProcessing || !currentTransform.crop?.width}
                className='bg-black text-white hover:bg-gray-800 w-full sm:flex-1'
              >
                {isProcessing ? 'Processing...' : 'Apply'}
              </Button>
            </div>
          </div>

          <div className='flex-1 min-h-0 order-1 lg:order-2'>
            <ImageEditor imageSrc={imageSrc} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ImageCropper
